import { afterNextRender, ErrorHandler, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, take, takeUntil } from 'rxjs/operators';
import { BdAuthenticationService } from './authentication.service';
import { Subject } from 'rxjs';
import { BD_FRONTEND_APP_CONFIG } from '@bindoc-projects/platform-lib/data';
import {
  BdAppConfigResponse,
  BdAuthErrorResponse,
} from '@bindoc-projects/app-config-domain-lib';
import { silentSessionRescueError } from './silent-session-rescue-error';
import { restoreRedirectTarget } from '../core/redirect-target';
import {
  currentFailedRedirect,
  resetFailedRedirect,
} from '../core/failed-redirect-guard';

/**
 * Mirrors the `loginFailedRoute` default in `app-config-handler.ts`; used only
 * when a deployment's frontend config omits it.
 */
const DEFAULT_LOGIN_FAILED_ROUTE = 'sys-login-failed';

export class BdAuthenticationRoot {
  public isIframe = false;
  public get loggedIn(): boolean {
    return this._authenticationService.state.status === 'authenticated';
  }
  protected readonly destroy$ = new Subject<void>();

  constructor(
    protected _msalGuardConfig: MsalGuardConfiguration,
    protected _msalBroadcastService: MsalBroadcastService,
    protected _msalService: MsalService,
    protected _authenticationService: BdAuthenticationService,
  ) {}

  public onDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected _initAuthenticationRoot(injector: Injector): void {
    // Defer iframe detection until after the first render so that during SSR
    // hydration `isIframe` stays `false` (matching the server-rendered DOM).
    // This prevents a hydration mismatch when the app loads inside an MSAL
    // hidden iframe for silent token renewal.
    afterNextRender(
      () => {
        this.isIframe = window !== window.parent && !window.opener;
      },
      { injector },
    );

    // As many Angular applications using standalone components are unable to bootstrap the MsalRedirectComponent,
    // handleRedirectObservable must be subscribed to directly.
    // `navigateToLoginRequestUrl: false` stops MSAL from hard-reloading the
    // app on the originally requested URL after processing the auth response
    // on the redirectUri page (a third full bootstrap per login) — the
    // original URL is restored through the router instead (redirect-target.ts).
    // This is the first handleRedirectPromise call of the page load, so its
    // options win the internal memoization over the MsalGuard's later call.
    // A failed auth response never reaches the subscription below: msal-browser
    // ignores an `#error=…` fragment unless one of its own interactions is
    // flagged in progress, so it resolves `null` instead of rejecting. The
    // failed-redirect guard detects it at instance creation — report it here,
    // where an ErrorHandler is reachable (BDA-5866).
    this._handleFailedRedirect(injector, currentFailedRedirect());

    this._msalService
      .handleRedirectObservable({ navigateToLoginRequestUrl: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result?.account) {
            // Genuine sign-in: drop the failure flag so a later
            // re-authentication in this tab is never blocked by a stale one.
            resetFailedRedirect();
            restoreRedirectTarget(injector.get(Router));
          }
        },
        // MSAL *does* reject when it processes a response it recognises as
        // failed (interaction flagged in progress). Previously unhandled, which
        // is why the identity provider's error never surfaced anywhere.
        error: (error: unknown) => {
          this._authenticationService.reportError(error);
          injector.get(ErrorHandler).handleError(error);
        },
      });

    // Once MSAL finished starting up (redirect response processed, cache
    // loaded), try to silently resurrect the previous session in case the
    // token cache did not survive a browser restart.
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this._attemptSilentSessionRescue(injector);
      });
  }

  /**
   * Reports a failed identity-provider redirect response and puts the user on
   * the page that explains it.
   *
   * The navigation is done here rather than left to the MsalGuard's
   * `loginFailedRoute`, because the provider returns to the app's `redirectUri`
   * — which is typically an unguarded landing route. No guard runs there, so
   * nothing would take the user to the error page: they were left on the
   * redirect target with an interactive login that silently refuses to start
   * (found in staging QA of BDA-5866). Driving it from here makes the terminal
   * state independent of which route the `redirectUri` happens to hit.
   */
  protected _handleFailedRedirect(
    injector: Injector,
    response: BdAuthErrorResponse | null,
  ): void {
    if (!response) {
      return;
    }
    this._authenticationService.reportError(response);
    this._reportFailedRedirect(injector, response);

    const route =
      this._msalGuardConfig.loginFailedRoute ?? DEFAULT_LOGIN_FAILED_ROUTE;
    const target = route.startsWith('/') ? route : `/${route}`;
    void injector.get(Router).navigateByUrl(target);
  }

  /**
   * Reports a failed identity-provider redirect response to Angular's
   * `ErrorHandler`, which the app-starter binds to `BdUserErrorHandler` →
   * `trackException` → Dash0 `reportError`. Without this the whole failure class
   * is invisible in telemetry and only surfaces as a support ticket.
   */
  protected _reportFailedRedirect(
    injector: Injector,
    response: BdAuthErrorResponse | null,
  ): void {
    if (!response) {
      return;
    }
    const correlation = response.correlationId
      ? ` (correlation id ${response.correlationId})`
      : '';
    injector
      .get(ErrorHandler)
      .handleError(
        new Error(
          `Authentication redirect failed: ${response.error}${correlation}. ${response.errorDescription}`.trim(),
        ),
      );
  }

  /**
   * msal-browser v5 encrypts the localStorage token cache with a key kept in
   * a session cookie, so every browser restart silently wipes all cached
   * accounts — only the `active-account-filters` marker survives. Without a
   * rescue, a returning user appears logged out until a guarded route bounces
   * the whole app through a full-page B2C redirect (two extra bootstraps;
   * measured at 8s+ on mid-range hardware) — and on public routes never
   * re-authenticates at all.
   *
   * If a previous session left the marker behind but MSAL knows no account,
   * attempt a single `ssoSilent` against the B2C SSO session cookie: a hidden
   * iframe with `prompt=none` re-establishes the session in ~1s without any
   * visible redirect. If the B2C session is gone too, fail silently — the
   * user is genuinely logged out and the interactive flows take over as
   * before.
   */
  protected _attemptSilentSessionRescue(injector: Injector): void {
    if (this._authenticationService.state.status === 'error') {
      return; // Do not hide an explicit redirect failure with a session rescue.
    }
    if (typeof window === 'undefined') {
      return; // SSR
    }
    if (window !== window.parent && !window.opener) {
      return; // running inside MSAL's own hidden iframe
    }
    const instance = this._msalService.instance;
    if (instance.getAllAccounts().length > 0) {
      return; // session intact — nothing to rescue
    }
    const clientId = instance.getConfiguration().auth.clientId;
    // No initializer: the catch returns, so the only readable value is the one
    // the try assigns (ESLint 10's no-useless-assignment).
    let marker: string | null;
    try {
      marker = window.localStorage.getItem(
        `msal.${clientId}.active-account-filters`,
      );
    } catch {
      return;
    }
    if (!marker) {
      // Never logged in on this browser — don't probe B2C for anonymous
      // visitors (public/SEO traffic would pay an iframe roundtrip per load).
      return;
    }
    const appConfig = injector.get<BdAppConfigResponse | null>(
      BD_FRONTEND_APP_CONFIG,
      null,
    );
    const silentRedirectUri = appConfig?.msalConfig?.silentRedirectUri;
    const correlationId = window.crypto.randomUUID();
    this._msalService
      .ssoSilent({
        correlationId,
        scopes: [clientId],
        ...(silentRedirectUri ? { redirectUri: silentRedirectUri } : {}),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result?.account) {
            this._authenticationService.selectAccount(result.account);
          }
        },
        error: (error: unknown) => {
          const diagnostic = silentSessionRescueError(error, correlationId);
          if (diagnostic) {
            injector.get(ErrorHandler).handleError(diagnostic);
          }
        },
      });
  }
}
